import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
  writeBatch,
  increment,
} from 'firebase/firestore';
import { db } from '../../config';
import { type ItemListType } from '../../../../types/common';
import { uploadPatternsToStorage, copyPatternsToStorage } from '../../storage';
import { patternStatus, shareScope } from '../../../../constants/enum';
import { getPlanLimits } from '../../../../constants/plans';
import { PatternFileType, type ShareScope } from '../../../../types/pattern';
import { getTotalItemCount } from '../../../../utils/pattern/validation';
import { convertItemListToSnakeCase } from '../../../../utils/pattern/itemListConverter';
import { ValidationError } from '../../../../modules/error';
import { recordClientAuditEvent } from '../../function/security/clientAuditEventFunction';
import { auditAction, auditResourceType } from '../../../../constants/enum';

/**
 * 読取パターンを作成（PatternFieldType用）
 */
export const postPattern = async (
  userId: string,
  data: {
    title: string;
    documentName?: string | null;
    description?: string | null;
    remarks?: string | null;
    fileType: number;
    images?: File[]; // 新規アップロードファイル → original_sample_files
    itemList: ItemListType[];
    existingOriginalFiles?: PatternFileType[]; // コピー時の元ファイル
    geminiModel?: number;
    ocrEngine?: number; // OCRエンジン設定（#3264 ocrEngineType）
    prompt?: string;
    useCustomPrompt?: boolean;
    companyId?: string;
    isProxy?: boolean;
    operatorId?: string;
    isClassificationTarget?: boolean;
    shareScope?: ShareScope;
    sharedGroupIds?: string[];
    sharedUserIds?: string[];
  },
): Promise<string> => {
  const patternsRef = collection(db, 'patterns');

  // まず読取パターンドキュメントを作成（IDを取得するため）
  const patternDoc = {
    pattern_name: data.title, // パターン名を保存
    document_name: data.documentName || '', // 書類の名称を保存（プロンプト内で使用）
    description: data.description || '', // 書類の説明を保存
    remarks: data.remarks || '',
    file_type: data.fileType, // ファイル処理タイプを追加
    item_count: getTotalItemCount(data.itemList),
    item_list: convertItemListToSnakeCase(data.itemList), // snake_caseに変換
    is_public: true,
    status: patternStatus.valid,
    ...(data.geminiModel !== undefined && { gemini_model: data.geminiModel }), // Geminiモデル設定を追加
    ...(data.ocrEngine !== undefined && { ocr_engine: data.ocrEngine }), // OCRエンジン設定を追加（#3264）
    ...(data.prompt !== undefined && { prompt: data.prompt }), // promptが指定されている場合のみ追加（空文字列も含む）
    ...(data.useCustomPrompt !== undefined && { use_custom_prompt: data.useCustomPrompt }), // useCustomPromptフラグを追加
    ...(data.companyId && { company_id: data.companyId }), // company_idを追加
    ...(data.isProxy !== undefined && { is_proxy: data.isProxy }), // 代理操作フラグを追加
    ...(data.operatorId && { operator_id: data.operatorId }), // 代理操作時の運営スタッフUIDを追加
    is_classification_target: data.isClassificationTarget !== false, // 自動仕分け対象フラグ（デフォルト: true）
    // 共有範囲（#2804 Phase2 / 限定共有の担当者指定 #2963）。既定は企業全体
    share_scope: data.shareScope ?? shareScope.company,
    shared_group_ids: data.shareScope === shareScope.group ? (data.sharedGroupIds ?? []) : [],
    shared_user_ids: data.shareScope === shareScope.group ? (data.sharedUserIds ?? []) : [],
    owner_user_id: userId, // 作成者（private 判定用）
    display_order: 0, // 新規パターンは先頭に配置
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
    created_by: userId,
    updated_by: userId,
  };

  // 読取パターン数上限チェック（削除済みを除いたアクティブ数をFirestoreから直接取得）
  if (data.companyId) {
    const companyDocRef = doc(db, 'companies', data.companyId);
    const companySnap = await getDoc(companyDocRef);
    if (companySnap.exists()) {
      const companyData = companySnap.data();
      const plan = (companyData.plan as number) ?? 0;
      const limits = getPlanLimits(plan);
      const patternLimit = (companyData.pattern_limit as number) ?? limits?.patternCount ?? 0;
      const patternsRef = collection(db, 'patterns');
      const activePatternSnap = await getDocs(
        query(
          patternsRef,
          where('company_id', '==', data.companyId),
          where('status', '==', patternStatus.valid),
        ),
      );
      const activeCount = activePatternSnap.size;
      if (activeCount >= patternLimit) {
        throw new ValidationError(`読取パターン数が上限（${patternLimit}件）に達しています。`);
      }
    }
  }

  // writeBatchでパターン作成とカウント更新をアトミックに実行
  const newPatternRef = doc(patternsRef);
  const batch = writeBatch(db);

  // 既存パターンの display_order を +1（新規パターンを先頭に挿入するため）
  if (data.companyId) {
    const existingPatternsQuery = query(patternsRef, where('company_id', '==', data.companyId));
    const existingPatterns = await getDocs(existingPatternsQuery);
    existingPatterns.forEach(existingDoc => {
      if (existingDoc.data().status === patternStatus.valid) {
        batch.update(existingDoc.ref, {
          display_order: increment(1),
        });
      }
    });
  }

  batch.set(newPatternRef, patternDoc);

  // 企業の読取パターン数をインクリメント
  if (data.companyId) {
    const companyDocRef = doc(db, 'companies', data.companyId);
    batch.update(companyDocRef, { current_pattern_count: increment(1) });
  }

  await batch.commit();

  const docRef = newPatternRef;
  const patternId = docRef.id;

  // ファイル処理（original_sample_files のみ）
  let originalSampleFiles: PatternFileType[] = [];

  try {
    // コピー時の元ファイルを新しい場所にコピー
    if (data.existingOriginalFiles && data.existingOriginalFiles.length > 0) {
      const copiedFiles = await copyPatternsToStorage(
        data.existingOriginalFiles,
        patternId,
        'originals',
        data.companyId,
      );
      originalSampleFiles = [...originalSampleFiles, ...copiedFiles];
    }

    // 新規アップロードファイルを保存
    if (data.images && data.images.length > 0) {
      const uploadedFiles = await uploadPatternsToStorage(
        data.images,
        patternId,
        'originals',
        data.companyId,
      );
      originalSampleFiles = [...originalSampleFiles, ...uploadedFiles];
    }
  } catch (error) {
    await updateDoc(docRef, { status: 99 });
    if (data.companyId) {
      try {
        const companyDocRef = doc(db, 'companies', data.companyId);
        await updateDoc(companyDocRef, { current_pattern_count: increment(-1) });
      } catch {
        // カウント復元の失敗で元のエラーが隠れないようにする
      }
    }
    throw error;
  }

  if (originalSampleFiles.length > 0) {
    try {
      await updateDoc(docRef, {
        original_sample_files: originalSampleFiles,
        updated_at: serverTimestamp(),
      });
    } catch (error) {
      await updateDoc(docRef, { status: 99 });
      if (data.companyId) {
        try {
          const companyDocRef = doc(db, 'companies', data.companyId);
          await updateDoc(companyDocRef, { current_pattern_count: increment(-1) });
        } catch {
          // カウント復元の失敗で元のエラーが隠れないようにする
        }
      }
      throw error;
    }
  }

  // 監査ログ（#2985）: パターン作成。fire-and-forget で主処理を遅らせない
  void recordClientAuditEvent({
    action: auditAction.create,
    resource_type: auditResourceType.readPattern,
    resource_id: patternId,
  });

  return patternId;
};
